import { Suspense, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Loader } from '@givebutter/primitives/loader';
import { Separator } from '@givebutter/primitives/separator';
import * as Tabs from '@givebutter/primitives/tabs';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import {
    CreateLetterProofStripeSessionParams,
    LetterProofStripeSessionResponse,
    createLetterProofStripeSession,
    sendLetterProof,
} from 'api/engage';
import { LetterOrderedProofs } from 'assets/js/pages/engage/letters/LettersOrderedProofs';
import {
    LETTER_FREE_PROOF_PER_MONTH,
    LETTER_START_COST,
    PLUS_LETTER_START_COST,
} from 'assets/js/pages/engage/letters/constants';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { AddressForm } from 'components/address-form/AddressForm';
import { AddressFormValues } from 'components/address-form/types';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import * as StripeCheckoutDialog from 'components/stripe-checkout-dialog';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { useFormServerErrors } from 'customHooks/useFormServerErrors';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface LetterMailProofDialogProps extends Dialog.DialogProps {}

interface LetterProofDialogFormValues extends AddressFormValues {
    name: string;
}

const LetterMailProofDialog = (props: LetterMailProofDialogProps) => {
    const { children, ...rest } = props;
    const toast = useToast();
    const currencySymbol = useCurrencySymbol();
    const { accountId, remainingFreeMailProof, hasGivebutterPlusAccess } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const { data: letter } = useCurrentLetter();
    const [isStripeDialogOpen, setIsStripeDialogOpen] = useState(false);
    const [stripeProofSessionResponse, setStripeProofSessionResponse] =
        useState<LetterProofStripeSessionResponse | null>(null);
    const canSendFreeMailProof = letter.can_send_free_mail_proof && remainingFreeMailProof > 0;

    const methods = Form.useGivebutterForm<LetterProofDialogFormValues>({
        defaultValues: {
            name: '',
            line1: '',
            line2: '',
            city: '',
            zip: '',
            stateUs: '',
            stateIntl: '',
            country: 'USA',
        },
    });

    const { control, handleSubmit, path, reset } = methods;
    const { setAddressErrors } = useFormServerErrors(methods);

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: async (values: LetterProofDialogFormValues) => {
            const sessionResp = await createLetterProofStripeSession(
                accountId,
                letter.id,
                transformFormToRequest(values),
            );

            if (sessionResp.checkout_details === null) {
                return createMailing(sessionResp.mail_piece.id);
            }

            setStripeProofSessionResponse(sessionResp);
            setIsStripeDialogOpen(true);
        },
        onError: (error: GivebutterAxiosError) => {
            setAddressErrors(error.response?.data.errors);
            toast.error(
                error.response?.data.message ?? (
                    <>
                        <span className="text-semibold">Whoops.</span> Something went wrong. Please
                        contact support.
                    </>
                ),
            );
        },
    });

    const { mutate: createMailing, isPending: isLoadingMailProofOrder } = useMutation({
        mutationFn: (mailPieceId: number) => sendLetterProof(accountId, letter.id, mailPieceId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageById(accountId, letter.id),
            });
            toast.success('Proof ordered successfully');
            reset();
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data.message ?? (
                    <>
                        <span className="text-semibold">Whoops.</span> Something went wrong. Please
                        contact support.
                    </>
                ),
            );
        },
    });

    return (
        <>
            <Dialog.Root key={key} {...rest}>
                {children}
                <Dialog.Portal>
                    <Dialog.Overlay />
                    <Dialog.Content className="max-w-[60rem] p-0">
                        <div className="bg-gray-50 px-8 py-4 text-xl font-medium">
                            Mail proof
                            <Dialog.Close />
                        </div>
                        <Separator className="bg-gray-200" />

                        <Tabs.Root
                            defaultValue={'newProof'}
                            className={'flex w-full flex-col gap-8 p-6'}
                            variant={'underline'}
                        >
                            <Tabs.List>
                                <Tabs.Trigger value={'newProof'}>New proof</Tabs.Trigger>
                                <Tabs.Trigger value={'orderedProofs'}>Ordered proofs</Tabs.Trigger>
                            </Tabs.List>
                            <Tabs.Content value={'newProof'}>
                                <Form.Provider {...methods}>
                                    <form
                                        onSubmit={e => {
                                            e.preventDefault();
                                            handleSubmit(values => submit(values))();
                                        }}
                                    >
                                        <div className="flex flex-col gap-8 lg:flex-row">
                                            <div className="flex w-full flex-col gap-4 lg:min-w-[31.25rem]">
                                                <Form.Container>
                                                    <Form.Label name={path('name')}>
                                                        Recipient name
                                                        <Form.LabelAsterisk />
                                                    </Form.Label>
                                                    <Form.TextInputRoot
                                                        control={control}
                                                        name={path('name')}
                                                        rules={{
                                                            required: 'Name is required',
                                                        }}
                                                    >
                                                        <Form.TextInputForm placeholder="Enter name" />
                                                    </Form.TextInputRoot>
                                                    <Form.Error name={path('name')} />
                                                </Form.Container>
                                                <AddressForm isRequired={true} />
                                            </div>
                                            <div className="flex w-full flex-col gap-4 self-start rounded-xl bg-gray-50 p-4">
                                                <div>
                                                    <div className="text-xl font-medium">Total</div>
                                                    <div className="flex items-center gap-4">
                                                        <div
                                                            className={`text-3xl font-medium text-gray-400 ${
                                                                canSendFreeMailProof
                                                                    ? 'line-through'
                                                                    : ''
                                                            }`}
                                                        >
                                                            {currencySymbol}
                                                            {maskDecimal(
                                                                hasGivebutterPlusAccess
                                                                    ? PLUS_LETTER_START_COST
                                                                    : LETTER_START_COST,
                                                            )}
                                                        </div>
                                                        {canSendFreeMailProof && (
                                                            <Badge size="sm" color="primary">
                                                                FREE!
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                {canSendFreeMailProof && (
                                                    <div>
                                                        <div className="font-medium">
                                                            Enjoy a proof on us!
                                                        </div>
                                                        <div className="text-sm">
                                                            We'll provide one proof per mailing so
                                                            you can see and feel your letter.{' '}
                                                            <span className="italic">
                                                                Max {LETTER_FREE_PROOF_PER_MONTH}{' '}
                                                                free proofs per month.
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex justify-end">
                                                    <Form.SubmitButton
                                                        disabled={
                                                            isLoading || isLoadingMailProofOrder
                                                        }
                                                    >
                                                        {canSendFreeMailProof ? 'Get' : 'Purchase'}{' '}
                                                        proof
                                                    </Form.SubmitButton>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </Form.Provider>
                            </Tabs.Content>
                            <Tabs.Content value={'orderedProofs'} className="min-h-60">
                                <Suspense fallback={<Loader />}>
                                    <LetterOrderedProofs />
                                </Suspense>
                            </Tabs.Content>
                        </Tabs.Root>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
            <StripeCheckoutDialog.Root
                clientSecret={stripeProofSessionResponse?.checkout_details?.client_secret ?? ''}
                open={isStripeDialogOpen}
                onOpenChange={open => setIsStripeDialogOpen(open)}
                onComplete={() => createMailing(stripeProofSessionResponse?.mail_piece.id ?? 0)}
            />
        </>
    );
};

const transformFormToRequest = (
    values: LetterProofDialogFormValues,
): CreateLetterProofStripeSessionParams => {
    const state = values.country === 'USA' ? values.stateUs : values.stateIntl;
    return {
        recipient_name: values.name,
        city: values.city,
        zipcode: values.zip,
        state: state,
        address_1: values.line1,
        address_2: values.line2,
        country: values.country,
    };
};

const LetterMailProofDialogTrigger = Dialog.Trigger;
const Root = LetterMailProofDialog;

const Trigger = LetterMailProofDialogTrigger;

export { LetterMailProofDialog, LetterMailProofDialogTrigger, Root, Trigger };
